import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { AutomationsChatPage } from '~portal/components/admin/automations/chat';

/**
 * @desc `?c=<conversationId>` keeps the open conversation shareable and survives a reload. The
 * list shows the viewer's own conversations by default (Tim, 22 Oct 2026); `?all=true` widens it
 * to every conversation the H2 team started, so a link to the team view stays shareable. A legacy
 * `?mine=true` needs no handling: Mine is the default. `?b=<builderId>` (from a builder's "Back to
 * Ario") scrolls the parent to that build's card.
 */
type ChatSearch = { c?: string; all?: true; b?: string };

/** @desc A query flag as the router or a hand-typed link spells it. */
const isOn = (value: unknown): boolean =>
  value === true || value === 'true' || value === 1 || value === '1';

export const Route = createFileRoute('/_protected/admin/ario/chat')({
  validateSearch: (search: Record<string, unknown>): ChatSearch => ({
    ...(typeof search.c === 'string' && /^[0-9a-f]{24}$/i.test(search.c) ? { c: search.c } : {}),
    ...(typeof search.b === 'string' && /^[0-9a-f]{24}$/i.test(search.b) ? { b: search.b } : {}),
    ...(isOn(search.all) ? { all: true as const } : {}),
  }),
  component: AutomationsChatRoute,
});

/** @desc Automations → Chat (H2-7, task 8.2). */
function AutomationsChatRoute() {
  const { c, all, b } = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });
  return (
    <AutomationsChatPage
      conversationId={c ?? null}
      {...(b ? { focusBuild: b } : {})}
      scope={all ? 'all' : 'mine'}
      onScopeChange={(scope) => {
        void navigate({
          search: (prev) => ({ ...prev, all: scope === 'all' ? true : undefined }),
          resetScroll: false,
        });
      }}
      onSelect={(id, options) => {
        // `b` (the build to show) belongs to one opening only: any other open drops it.
        void navigate({
          search: (prev) => ({ ...prev, c: id, b: options?.build }),
          resetScroll: false,
        });
      }}
    />
  );
}
